import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransactions } from '../redux/actions/thunks/Storage';
import LoadingIcon from './LoadingIcon';

// Build a readable label for each item type so the summary list is human-friendly.
// Transactions come in multiple shapes (regular stock, hand rails, "other" items),
// so we normalize the display text here to avoid duplicating formatting logic.
const formatItemLabel = (item) => {
  if (item.isOther) {
    return `${item.item} ${item.info}`.trim();
  }

  if (item.isHandRail) {
    return `${item.handRail_woodType} ${item.model}, ${item.length}mm`;
  }

  return `${item.woodType} ${item.length}x${item.depth}x${item.thickness}mm`;
};

const buildSortKey = (item) => {
  if (item.isHandRail) {
    return {
      woodType: item.handRail_woodType || '',
      thickness: Number(item.thickness) || 0,
      length: Number(item.length) || 0,
      handrailModel: item.model || '',
    };
  }

  return {
    woodType: item.woodType || '',
    thickness: Number(item.thickness) || 0,
    length: Number(item.length) || 0,
    handrailModel: item.model || '',
  };
};

const normalizeString = (value) => (value || '').toString().toLowerCase();

const compareByName = (entryA, entryB) => {
  const woodTypeComparison = normalizeString(entryA.sortKey.woodType).localeCompare(
    normalizeString(entryB.sortKey.woodType),
    'fi',
    { sensitivity: 'base' },
  );

  if (woodTypeComparison !== 0) {
    return woodTypeComparison;
  }

  const thicknessComparison = entryA.sortKey.thickness - entryB.sortKey.thickness;
  if (thicknessComparison !== 0) {
    return thicknessComparison;
  }

  const lengthComparison = entryA.sortKey.length - entryB.sortKey.length;
  if (lengthComparison !== 0) {
    return lengthComparison;
  }

  return normalizeString(entryA.sortKey.handrailModel).localeCompare(
    normalizeString(entryB.sortKey.handrailModel),
    'fi',
    { sensitivity: 'base' },
  );
};

// Translate a transaction into a signed quantity change.
//  - "Poistettu" means items were taken from storage (negative)
//  - "Uusi" means new items were added (positive)
//  - "Muokattu" compares the new amount with the previous amount
// The resulting value allows us to aggregate consumption by summing negatives.
const calculateNetChange = (transaction) => {
  if (transaction.action === 'Poistettu') {
    return -transaction.amount;
  }

  if (transaction.action === 'Uusi') {
    return transaction.amount;
  }

  if (transaction.action === 'Muokattu' && transaction.oldItem) {
    return transaction.amount - transaction.oldItem.amount;
  }

  return transaction.amount;
};

const finnishMonths = [
  'Tammikuuta',
  'Helmikuuta',
  'Maaliskuuta',
  'Huhtikuuta',
  'Toukokuuta',
  'Kesäkuuta',
  'Heinäkuuta',
  'Elokuuta',
  'Syyskuuta',
  'Lokakuuta',
  'Marraskuuta',
  'Joulukuuta',
];

const formatDateFinnish = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(`${value}T00:00:00`);
  const monthName = finnishMonths[date.getMonth()] || '';

  return `${date.getDate()}. ${monthName} ${date.getFullYear()}`;
};

// SummaryWindow renders a modal overlay that lets the user pick a date range and
// view how many units of each item type were consumed within that period. It
// requests transactions scoped to the selected window, aggregates only the
// negative (outgoing) changes, and lists totals per item alongside an overall
// consumption sum.
const SummaryWindow = ({ onClose }) => {
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.storage.transactions);
  const loading = useSelector((state) => state.storage.loading);

  const [startDateInput, setStartDateInput] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDateInput, setEndDateInput] = useState(() => new Date().toISOString().split('T')[0]);

  const [startDate, setStartDate] = useState(startDateInput);
  const [endDate, setEndDate] = useState(endDateInput);
  const [sortBy, setSortBy] = useState('amount');
  const [searchTerm, setSearchTerm] = useState('');

  const hasPendingDateChange = startDateInput !== startDate || endDateInput !== endDate;

  const invalidRange = useMemo(
    () => !startDateInput || !endDateInput || new Date(startDateInput) > new Date(endDateInput),
    [endDateInput, startDateInput],
  );

  const applyDateRange = () => {
    if (!invalidRange) {
      setStartDate(startDateInput);
      setEndDate(endDateInput);
    }
  };

  // Fetch transactions for the selected date range.
  useEffect(() => {
    if (startDate && endDate && new Date(startDate) <= new Date(endDate)) {
      dispatch(fetchTransactions({ startDate, endDate }));
    }
  }, [dispatch, endDate, startDate]);

  const filteredTransactions = useMemo(() => {
    if (invalidRange) {
      return [];
    }

    return transactions;
  }, [invalidRange, transactions]);

  // Aggregate only the negative (consumed) amounts per item label, preserving
  // the total consumed quantity for the entire window. We calculate the net
  // change first, then collect counts under a consistent label produced by
  // formatItemLabel.
  const consumptionSummary = useMemo(() => {
    return filteredTransactions.reduce((summary, transaction) => {
      const netChange = calculateNetChange(transaction);

      if (netChange < 0) {
        const label = formatItemLabel(transaction.item);
        const consumedAmount = Math.abs(netChange);

        summary[label] = summary[label] || { amount: 0, sortKey: buildSortKey(transaction.item) };
        summary[label].amount += consumedAmount;
      }

      return summary;
    }, {});
  }, [filteredTransactions]);

  // Sum all consumed amounts to show an overall total for the selected range.
  const totalConsumption = useMemo(() => {
    return Object.values(consumptionSummary).reduce((total, { amount }) => total + amount, 0);
  }, [consumptionSummary]);

  const summaryEntries = useMemo(() => {
    const entries = Object.entries(consumptionSummary).map(([label, data]) => ({
      label,
      amount: data.amount,
      sortKey: data.sortKey,
    }));

    const filteredEntries = entries.filter(({ label }) =>
      normalizeString(label).includes(normalizeString(searchTerm)),
    );

    if (sortBy === 'name') {
      return filteredEntries.sort(compareByName);
    }

    return filteredEntries.sort((entryA, entryB) => entryB.amount - entryA.amount);
  }, [consumptionSummary, searchTerm, sortBy]);

  const formattedDateRange = useMemo(
    () => `${formatDateFinnish(startDate)} - ${formatDateFinnish(endDate)}`,
    [endDate, startDate],
  );

  const sortedEntriesByName = useMemo(
    () => [...summaryEntries].sort(compareByName),
    [summaryEntries],
  );

  const handleExport = () => {
    if (sortedEntriesByName.length === 0) {
      return;
    }

    const title = `Tuotteiden kulutus ajanjaksolle: ${formatDateFinnish(
      startDate,
    )} - ${formatDateFinnish(endDate)}`;

    const rows = [
      [title],
      ['Nimi', 'Määrä'],
    ];

    sortedEntriesByName.forEach((entry) => {
      rows.push([entry.label, entry.amount]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Tyylit toimivat vain, jos käytössä on tyylejä tukeva buildi.
    // Jos ei ole, tämä objekti yksinkertaisesti ignoroidaan.
    if (worksheet.A1) {
      worksheet.A1.s = {
        font: { bold: true, sz: 14 },
      };
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Yhteenveto');

    const filename = `Yhteenveto_${startDate}_${endDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-window modal-window--wide modal-window--tall">
        <div className="modal-header modal-header--between">
          <h2 className="modal-title">Yhteenveto</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            Sulje
          </button>
        </div>

        <div className="Summary-Filters">
          <div className="Summary-DateInput">
            <label htmlFor="start-date">Alkaen</label>
            <input
              id="start-date"
              type="date"
              value={startDateInput}
              onChange={(event) => setStartDateInput(event.target.value)}
            />
          </div>
          <div className="Summary-DateInput">
            <label htmlFor="end-date">Päättyen</label>
            <input
              id="end-date"
              type="date"
              value={endDateInput}
              onChange={(event) => setEndDateInput(event.target.value)}
            />
          </div>
          <div className="Summary-ControlGroup">
            <label htmlFor="summary-search">Haku</label>
            <input
              id="summary-search"
              type="text"
              value={searchTerm}
              placeholder="Etsi nimellä"
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="Summary-ControlGroup">
            <label htmlFor="summary-sort">Järjestys</label>
            <select
              id="summary-sort"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
            >
              <option value="amount">Määrä</option>
              <option value="name">Nimi</option>
            </select>
          </div>
          <div className="Summary-ActionButtons">
            <button
              className="btn btn-primary"
              onClick={applyDateRange}
              disabled={invalidRange}
            >
              Hae
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={invalidRange || hasPendingDateChange || sortedEntriesByName.length === 0}
            >
              Vie Exceliin
            </button>
          </div>
        </div>

        <div className="Summary-ListContainer">
          {loading ? (
            <div className="Summary-ListPlaceholder">
              <LoadingIcon />
            </div>
          ) : (
            <>
              {invalidRange ? (
                <p className="Summary-Empty">Aloituspäivän tulee olla ennen lopetuspäivää.</p>
              ) : hasPendingDateChange ? (
                <p className="Summary-Empty">Klikkaa &quot;Hae&quot; päivittääksesi tulokset.</p>
              ) : (
                <>
                  <div className="Summary-Totals">
                    <span>{formattedDateRange}</span>
                    <span>Kulutus yhteensä: {totalConsumption} kpl</span>
                  </div>
                  {Object.keys(consumptionSummary).length === 0 ? (
                    <p className="Summary-Empty">Ei kulutusta valitulla aikavälillä.</p>
                  ) : summaryEntries.length === 0 ? (
                    <p className="Summary-Empty">Ei hakutuloksia.</p>
                  ) : (
                    <ul className="Summary-List">
                      {summaryEntries.map(({ label, amount }) => (
                        <li key={label} className="Summary-Item">
                          <span className="Summary-Label">{label}</span>
                          <span className="Summary-Amount">-{amount} kpl</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryWindow;
